import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Switch,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Autocomplete,
  Divider,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  FiberManualRecord as DotIcon,
  Save as SaveIcon,
  HelpOutline as HelpIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { retailerApi, badgeFairnessApi, apiKeysApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';
import BillingEntityPanel from './BillingEntityPanel';
import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  COUNTRY_NAME_BY_CODE,
  LANGUAGE_NAME_BY_CODE,
} from '../../constants/geo';

// How many hours before a heartbeat is considered stale
const STALE_HOURS = 24;

// Allowed retailer.status values — must match the DB CHECK constraint
// retailers_status_check (active, inactive, pending, prospect, testing).
const RETAILER_STATUSES = ['active', 'prospect', 'testing', 'pending', 'inactive'];
// Business model — a store carrying many brands vs a single brand's own store. This is
// NOT the platform (that's feed_source / storefront_platform); it feeds code-extraction
// prompts. UNKNOWN exists in legacy/prospect rows as an unset placeholder but is NOT an
// offerable choice — you can always tell which of the two a retailer is at a glance.
const RETAILER_TYPES = ['MULTI_BRAND', 'DIRECT_BRAND'];
// How stock/catalogue is INGESTED — drives the correction regime in StockReadinessPanel
// (channable = crowd-arbitrated DOM pre-flight; shopware/shopify = live platform API;
// magento = intra-day mini-parser). Must match RetailerService.ALLOWED_FEED_SOURCES on
// the backend. Distinct from storefront_platform (how the PDP RENDERS). null = unset.
const FEED_SOURCES = ['channable', 'shopware', 'shopify', 'magento'];

const STATUS_COLORS = {
  active: 'success',
  prospect: 'info',
  testing: 'secondary',
  pending: 'warning',
  inactive: 'error',
};

// Trust overlay intro line (migration 178; spec overlay/trust-overlay-redesign.md §5.7).
// The line sits in two lines of Liter 12 px / 1.55 on the 186 px mobile card. Measured
// 2026-09-22: 64 width units fit, an emoji counts as 3. 60 is the guideline given to retailers.
const INTRO_LINE_MAX_UNITS = 64;
const INTRO_LINE_ADVISED_UNITS = 60;
const OVERLAY_RENDERERS = ['legacy', 'trust'];

function introLineUnits(line) {
  const chars = [...(line || '')];
  const emoji = chars.filter((ch) => /\p{Extended_Pictographic}/u.test(ch)).length;
  return chars.length + 2 * emoji;
}

// ---------------------------------------------------------------------------
// Retailer API keys (spec dashboard/buyer-insights-api-and-mcp.md §3): read-only keys a retailer
// uses from Make and from a Claude custom connector, issued here on request. The key is shown once
// at creation; the list shows prefix, label, created, last used and how (claude / make / direct).
// ---------------------------------------------------------------------------
function ApiKeysPanel({ retailer }) {
  const [keys, setKeys] = useState(null);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { key, label } — shown once
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setError(null);
    apiKeysApi
      .list(retailer.retailer_id)
      .then((res) => (res.success ? setKeys(res.data) : setError(res.error || 'Keys failed to load')))
      .catch((e) => setError(e.message || 'Keys failed to load'));
  }, [retailer.retailer_id]);

  useEffect(() => {
    setKeys(null);
    setCreated(null);
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiKeysApi.create(retailer.retailer_id, label.trim());
      if (!res.success) throw new Error(res.error || 'Could not create the key');
      setCreated({ key: res.data.key, label: res.data.label });
      setLabel('');
      setCopied(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.label}" (${k.key_prefix}…)? Make scenarios and Claude connectors using it stop at once.`)) return;
    setError(null);
    try {
      const res = await apiKeysApi.revoke(retailer.retailer_id, k.id);
      if (!res.success) throw new Error(res.error || 'Could not revoke the key');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const via = { chatgpt: 'ChatGPT', claude: 'Claude', mcp: 'AI assistant', make: 'Make', direct: 'direct' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>API keys</Typography>
        <Tooltip
          title="Read-only keys for the retailer's own automations (Make) and Claude connector. They read the Buyer Insights data only, for this retailer and its sister stores, and never expire: revoke them here. The key is shown once."
          arrow
        >
          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        </Tooltip>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Label (e.g. Make — Monday list)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ minWidth: 280 }}
        />
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={create} disabled={creating || !label.trim()}>
          Create key
        </Button>
      </Stack>
      {created && (
        <Alert severity="success" sx={{ mt: 1 }} onClose={() => setCreated(null)}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Key for “{created.label}”. Copy it now: it is not shown again.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
              {created.key}
            </Typography>
            <Button size="small" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
          </Stack>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {keys === null && !error && <LinearProgress sx={{ mt: 1 }} />}
      {keys && keys.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>No keys yet.</Typography>
      )}
      {keys && keys.length > 0 && (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id} sx={{ opacity: k.revoked_at ? 0.5 : 1 }}>
                  <TableCell>{k.label}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{k.key_prefix}…</TableCell>
                  <TableCell>{when(k.created_at)} · {k.created_by}</TableCell>
                  <TableCell>
                    {k.revoked_at
                      ? `Revoked ${when(k.revoked_at)}`
                      : k.last_used_at
                        ? `${when(k.last_used_at)} via ${via[k.last_used_via] || k.last_used_via || '—'}`
                        : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    {!k.revoked_at && (
                      <Tooltip title="Revoke" arrow>
                        <IconButton size="small" color="warning" onClick={() => revoke(k)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// Balance limit (spec overlay/balance-control.md §3.2, §3.3, §3.7). One band W in orders on the
// retailer's 30-day net position, set by HandOff on the retailer's request after they opted in.
// Saving re-stamps the latest ledger row, so the band applies within a minute (no nightly wait).
// The §3.7 exposure table is fetched for the edge the position points to and shown on request:
// it is the pre-grant check. There is no notes field (v1.2, Daniel 2026-09-25): the grant is audited
// server-side, the disclosure lives in the retailer-comms record, and the cost of a band is what the
// retailer will see on their own dashboard once the setting is theirs.
// ---------------------------------------------------------------------------
function BalanceBandPanel({ retailer, onChanged }) {
  const [band, setBand] = useState(retailer.balance_band_orders == null ? '' : String(retailer.balance_band_orders));
  const [exposure, setExposure] = useState(null);
  const [exposureError, setExposureError] = useState(null);
  const [showExposure, setShowExposure] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setBand(retailer.balance_band_orders == null ? '' : String(retailer.balance_band_orders));
  }, [retailer.retailer_id, retailer.balance_band_orders]);

  useEffect(() => {
    let cancelled = false;
    setExposure(null);
    setExposureError(null);
    badgeFairnessApi
      .getExposure(retailer.retailer_id)
      .then((res) => {
        if (cancelled) return;
        if (res.success) setExposure(res.data);
        else setExposureError(res.error || 'Exposure table failed to load');
      })
      .catch((e) => {
        if (!cancelled) setExposureError(e.message || 'Exposure table failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [retailer.retailer_id]);

  const dirty = band !== (retailer.balance_band_orders == null ? '' : String(retailer.balance_band_orders));

  const save = async (clear) => {
    const value = clear ? null : Number(band);
    if (!clear && (band.trim() === '' || !Number.isInteger(value) || value < 10)) {
      setError('The band is a whole number of orders, at least 10. Use "Clear band" to switch it off.');
      return;
    }
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const res = await badgeFairnessApi.setBalanceBand(retailer.retailer_id, { band_orders: value });
      if (!res.success) {
        setError(res.error || 'Save failed');
        return;
      }
      setResult(res.data.restamp);
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.body?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const self = exposure?.self;
  const net = self?.net_30d_orders;
  const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const stateLine = (() => {
    if (result) {
      if (result.inbound_throttled) return `Inbound throttle engaged now (snapshot ${result.snapshot_date}, net ${result.net_30d_orders > 0 ? '+' : ''}${result.net_30d_orders}). Partners stop showing this retailer within a minute.`;
      if (result.outbound_throttled) return `Outbound throttle engaged now (snapshot ${result.snapshot_date}, net ${result.net_30d_orders > 0 ? '+' : ''}${result.net_30d_orders}). Their own pages show no overlay within a minute.`;
      if (result.balance_control_enabled === false) return 'Saved, but BALANCE_CONTROL_ENABLED is off: nothing is enforced.';
      return `Saved. Within band on snapshot ${result.snapshot_date} (net ${result.net_30d_orders > 0 ? '+' : ''}${result.net_30d_orders}); no throttle.`;
    }
    if (result === null && self == null) return null;
    return null;
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>Balance limit</Typography>
        <Tooltip
          arrow
          title="One number, in orders, on the 30-day gap between orders received and sent (approved commission only). Past +band the retailer is hidden from partners' carousels; past −band its own pages get no overlay. Releases by itself at 80% of the band. Set only after the retailer asked for it and was told what it does (spec §3.6); the exposure table below is the pre-grant check (§3.7)."
        >
          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {self
          ? `Net position ${net == null ? '—' : `${net > 0 ? '+' : ''}${net}`} orders on ${self.snapshot_date}` +
            (self.inbound_throttled ? ' · inbound throttled' : '') +
            (self.outbound_throttled ? ' · outbound throttled' : '') +
            '. A saved band applies within a minute; a cleared one lifts the throttle within a minute.'
          : 'No ledger row yet for this retailer; a band saved now is applied by the next nightly capture.'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: 2, alignItems: 'start' }}>
        <TextField
          size="small"
          label="Band (orders, ≥ 10; empty = off)"
          value={band}
          onChange={(e) => setBand(e.target.value)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          fullWidth
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" onClick={() => save(false)} disabled={saving || !dirty}>
          Save band
        </Button>
        <Button
          size="small"
          color="warning"
          onClick={() => save(true)}
          disabled={saving || retailer.balance_band_orders == null}
        >
          Clear band
        </Button>
        <Button size="small" onClick={() => setShowExposure((v) => !v)} disabled={!exposure && !exposureError}>
          {showExposure ? 'Hide exposure table' : `Show exposure table${exposure ? ` (${exposure.side})` : ''}`}
        </Button>
      </Stack>
      {stateLine && (
        <Alert severity={result?.inbound_throttled || result?.outbound_throttled ? 'warning' : 'success'} sx={{ mt: 1 }}>
          {stateLine}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {showExposure && exposureError && <Alert severity="warning" sx={{ mt: 1 }}>{exposureError}</Alert>}
      {showExposure && exposure && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {exposure.side === 'inbound'
              ? 'Share of each partner’s sends that go to this retailer (what an inbound throttle removes from them); sole = the calls it would empty.'
              : 'Share of each partner’s receives that come from this retailer (what an outbound throttle removes from them).'}{' '}
            Other live bands:{' '}
            {exposure.live_bands?.length
              ? exposure.live_bands.map((b) => `${b.retailer_id} ${b.band_orders}`).join(', ')
              : 'none'}
            .
          </Typography>
          <TableContainer sx={{ overflowX: 'auto', maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">Partner total</TableCell>
                  <TableCell align="right">Share</TableCell>
                  <TableCell align="right">Sole-candidate share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exposure.partners.map((x) => (
                  <TableRow key={x.partner}>
                    <TableCell>{x.partner}</TableCell>
                    <TableCell align="right">{x.clicks.toLocaleString()}</TableCell>
                    <TableCell align="right">{x.partner_total.toLocaleString()}</TableCell>
                    <TableCell align="right">{pct(x.share_of_partner_flow)}</TableCell>
                    <TableCell align="right">{pct(x.sole_candidate_share_of_partner_flow)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

function IntroLineField({ value, onChange, label = 'Overlay intro line' }) {
  useEffect(() => {
    if (document.getElementById('handoff-liter-font')) return;
    const link = document.createElement('link');
    link.id = 'handoff-liter-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Liter&display=swap';
    document.head.appendChild(link);
  }, []);
  const units = introLineUnits(value);
  const tooLong = units > INTRO_LINE_MAX_UNITS;
  return (
    <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <TextField
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={tooLong}
        helperText={`${units} / ${INTRO_LINE_MAX_UNITS} units (emoji = 3; aim for ${INTRO_LINE_ADVISED_UNITS} or less)`}
        fullWidth
      />
      {/* Preview at the width that binds: the 186 px mobile card text column. */}
      <Box
        sx={{
          width: 186, flexShrink: 0, p: 0, fontFamily: "'Liter', Arial, Helvetica, sans-serif",
          fontSize: 12, lineHeight: 1.55, color: '#5b5b5b', textAlign: 'center',
          outline: `1px dashed ${tooLong ? '#d32f2f' : '#ddd'}`, minHeight: 37,
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'unknown';
}

function StatusChip({ status }) {
  const color = STATUS_COLORS[status] || 'default';
  return <Chip label={capitalize(status)} size="small" color={color} variant="outlined" />;
}

function formatTimeAgo(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function HeartbeatStatus({ latestAt, heartbeatPages }) {
  if (!latestAt) {
    return <Chip label="No heartbeat" size="small" color="default" variant="outlined" />;
  }

  const hoursAgo = (Date.now() - new Date(latestAt).getTime()) / (1000 * 60 * 60);
  const isActive = hoursAgo < STALE_HOURS;
  const pages = heartbeatPages || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DotIcon sx={{ fontSize: 10, color: isActive ? 'success.main' : 'warning.main' }} />
        <Typography variant="caption" fontWeight={600} color={isActive ? 'success.main' : 'warning.main'}>
          {isActive ? 'Active' : 'Stale'}
        </Typography>
      </Box>
      {pages.map((p) => (
        <Typography key={p.page_type} variant="caption" color="text.secondary" sx={{ pl: 2 }}>
          {p.page_type}: {formatTimeAgo(p.last_seen_at)}
        </Typography>
      ))}
    </Box>
  );
}

function OverrideToggle({ retailerId, isEnabled, onToggle }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleToggle = () => {
    // Both enable and disable require confirmation
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    const newDisabled = isEnabled; // if currently enabled, we're disabling
    try {
      await retailerApi.setOverride(
        retailerId,
        newDisabled,
        reason || (newDisabled ? 'Disabled via admin dashboard' : 'Re-enabled via admin dashboard')
      );
      onToggle();
    } catch (err) {
      console.error('Failed to set override:', err);
    }
    setLoading(false);
    setDialogOpen(false);
    setReason('');
  };

  const actionLabel = isEnabled ? 'Disable' : 'Enable';
  const actionColor = isEnabled ? 'error' : 'success';

  return (
    <>
      <Tooltip title={isEnabled ? 'Widget active — click to disable' : 'Widget disabled — click to enable'}>
        <span>
          <Switch
            checked={isEnabled}
            onChange={handleToggle}
            disabled={loading}
            color={isEnabled ? 'success' : 'default'}
            size="small"
          />
        </span>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{actionLabel} widget for {retailerId}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isEnabled
              ? 'This will immediately stop the Handoff widget from loading on this retailer\'s site.'
              : 'This will re-enable the Handoff widget on this retailer\'s site.'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason (optional)"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isEnabled ? 'e.g. Retailer requested temporary disable' : 'e.g. Issue resolved'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setReason(''); }}>Cancel</Button>
          <Button onClick={handleConfirm} color={actionColor} variant="contained">
            {actionLabel} Widget
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// One direction's readiness result (Phase 4 symmetric). `block` is the per-direction object
// the API returns under result.veto / result.instock: {verdict, confirmed, contradicted,
// stale, sampled, candidates_total, contradiction_rate, contradicted_rows}. Guarded: an
// absent block (older API, or a direction with nothing to check) renders a neutral note
// instead of throwing — the crash this replaced was reading .verdict off undefined.
function DirectionResult({ title, expectation, block, sx }) {
  if (!block) {
    return (
      <Box sx={sx}>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        <Typography variant="body2" color="text.secondary">No result for this direction.</Typography>
      </Box>
    );
  }
  const rate = typeof block.contradiction_rate === 'number' ? block.contradiction_rate : 0;
  const classifiable = (block.confirmed ?? 0) + (block.contradicted ?? 0);
  // A verdict='pass' on ZERO classifiable candidates parsed nothing — it is NOT evidence the
  // parser works, so show it as INCONCLUSIVE (grey), not a green pass. This is the common
  // state for an already-enabled retailer: every would-be veto is already applied, so the
  // preflight (which samples only un-acted candidates) finds nothing to check. The go-live
  // gate (c) rejects such a vacuous pass server-side; the UI must not imply "ready" either.
  const inconclusive = block.verdict === 'pass' && classifiable === 0;
  const verdictLabel = inconclusive ? 'INCONCLUSIVE' : (block.verdict || 'unknown').toUpperCase();
  const verdictColor = inconclusive ? 'default' : block.verdict === 'pass' ? 'success' : 'error';
  return (
    <Box sx={sx}>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" color={verdictColor} label={`Verdict: ${verdictLabel}`} />
        <Chip size="small" color="success" variant="outlined" label={`confirmed ${block.confirmed ?? 0}`} />
        <Chip
          size="small"
          color={block.contradicted > 0 ? 'error' : 'default'}
          variant="outlined"
          label={`contradicted ${block.contradicted ?? 0}`}
        />
        <Chip size="small" variant="outlined" label={`stale ${block.stale ?? 0}`} />
        <Typography variant="body2" color="text.secondary">
          {block.sampled ?? 0} of {block.candidates_total ?? 0} candidates · {(rate * 100).toFixed(0)}% contradicted
        </Typography>
      </Stack>

      {block.contradicted_rows?.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>{expectation}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {block.contradicted_rows.map((row, i) => (
              <li key={i}>
                <a href={row.source_url} target="_blank" rel="noopener noreferrer">{row.source_url}</a>
                {' '}— size <strong>{row.size}</strong>
                {row.raw_color ? ` (${row.raw_color})` : ''}
                {row.sessions ? `, ${row.sessions} sessions` : ''}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {inconclusive && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No candidates to check right now — this validated nothing. For an already-enabled
          retailer, the live check that matters is the weekly <strong>drift</strong> check of
          active vetoes (chips above). This does not satisfy the SO→AV go-live gate.
        </Typography>
      )}
    </Box>
  );
}

// One gate row: a labelled pass/fail with the actual number behind it.
function GateRow({ label, ok, detail }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
      <Chip
        size="small"
        color={ok ? 'success' : 'warning'}
        variant={ok ? 'filled' : 'outlined'}
        label={ok ? '✓' : '✗'}
        sx={{ minWidth: 32 }}
      />
      <Typography variant="body2" sx={{ minWidth: 150 }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Stack>
  );
}

const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Channable (feed) regime: the crowd IS the decider, so show the crowd gates — the SO→AV
// audit go-live gate sub-flags — plus the live-PDP check as an on-demand read. Since
// 2026-09-22 the pre-flight is NOT a gate: enabling AV→SO no longer requires a passing run
// (every run ever passed; the checker's value is its weekly independent drift read).
function ChannableGates({ gate }) {
  const avSo = gate?.av_so;
  const soAv = gate?.so_av;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>Veto → sold-out (AV→SO) — live-PDP check (on demand, not a gate)</Typography>
      {avSo ? (
        <GateRow
          label="Last live-PDP read"
          ok={avSo.verdict === 'pass'}
          detail={`${avSo.verdict}${avSo.run_at ? ` · ${new Date(avSo.run_at).toLocaleDateString()}` : ''}`}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          No live-PDP read in the last 7 days — optional; run one below to spot-check the crowd against the page.
        </Typography>
      )}

      <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Veto → in-stock (SO→AV) — go-live gate</Typography>
      {soAv ? (
        <>
          <GateRow label="Trend maturity" ok={soAv.mature} detail={`${soAv.mature_days}/${soAv.window_days} days (${soAv.soav_days} present)`} />
          <GateRow
            label={`Confirmed share ≥ ${pct(soAv.min_confirmed_share)}`}
            ok={soAv.share_ok}
            detail={`${pct(soAv.confirmed_share)} — ${soAv.confirmed} confirmed / ${soAv.contradicted} contradicted / ${soAv.feed_blind} never re-listed`}
          />
          <GateRow label={`Sample ≥ ${soAv.min_sample}`} ok={soAv.sample_ok} detail={`${soAv.sample} committed decisions`} />
          <Typography variant="caption" color={soAv.cleared ? 'success.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
            {soAv.cleared ? 'SO→AV cleared — serving crowd in-stock overrides.' : 'SO→AV still in shadow until all three conditions pass.'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Diagnostic (not a gate): {pct(soAv.diagnostics?.false_avail_readings)} of readings on feed-sold-out sizes showed the size available — single-session noise plus feed lag on restocks; a sudden jump is worth a look.
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">No SO→AV audit data yet (needs ~2 weeks of shadow measurement).</Typography>
      )}
    </Box>
  );
}

// magento (mini-parser) regime: stock corrections are parser-owned — no vetoes at all. Show
// mini-parser health instead: raw_sku key coverage + when stock last changed.
function MagentoParserStatus({ status }) {
  if (!status) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>Availability mini-parser health</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Stock freshness is parser-owned: the mini-parser polls Magento MSI every 30 min (07:00–22:30)
        and writes in_stock_overall directly. Crowd vetoes never apply here — the toggle below
        governs whether the mini-parser&apos;s intra-day refresh runs for this retailer (OFF =
        nightly-only stock).
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={status.coverage_pct >= 95 ? 'success' : 'warning'}
          variant="outlined"
          label={`poll-key coverage ${status.coverage_pct ?? '—'}% (${status.keyed_sizes}/${status.active_sizes} active sizes)`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`last stock change ${status.last_stock_change_at ? new Date(status.last_stock_change_at).toLocaleString() : 'never'}`}
        />
      </Stack>
    </Box>
  );
}

// live_api (Shopware/Shopify) regime: the platform API arbitrates — no crowd gates. Show API
// health instead: last-24h API decisions, current overrides, last time we verified.
function LiveApiStatus({ status }) {
  const d = status?.decisions_24h || {};
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>Platform API health</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        The API decides — no crowd-accuracy gate. The crowd only triggers a check.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" label={`${d.total || 0} API decisions (24h)`} />
        <Chip size="small" color="warning" variant="outlined" label={`masked ${d.masked || 0}`} />
        <Chip size="small" color="success" variant="outlined" label={`cleared ${d.cleared || 0}`} />
        <Chip size="small" color="info" variant="outlined" label={`activated ${d.activated || 0}`} />
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        <Chip size="small" color="warning" label={`${status?.active_vetoes || 0} active vetoes`} />
        <Chip size="small" color={status?.active_instock > 0 ? 'info' : 'default'} variant={status?.active_instock > 0 ? 'filled' : 'outlined'} label={`${status?.active_instock || 0} in-stock overrides`} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Last verified against the API: {status?.last_verified_at ? new Date(status.last_verified_at).toLocaleString() : 'never'}.
      </Typography>
    </Box>
  );
}

// Stock-veto readiness panel — REGIME-AWARE. The apply-intra-day logic differs by feed_source:
// channable (crowd IS decider) shows the crowd gates + DOM pre-flight; shopware/shopify (API
// arbitrates) shows API health + a live get_stock spot-check. Hosts the apply_intraday_stock_vetoes
// toggle; the enable gate is server-side and channable-only.
function StockReadinessPanel({ retailer, onChanged, applyEnabled, onToggleApply, onSaveApply, applyDirty, saving }) {
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [gate, setGate] = useState(null);

  const feedSource = retailer.feed_source;
  const isLiveApi = feedSource === 'shopware' || feedSource === 'shopify';
  const isChannable = feedSource === 'channable';
  const isMagento = feedSource === 'magento';
  // magento shares the API spot-check button/result shape with live_api (keyed by raw_sku vs gtin).
  const isApiCheck = isLiveApi || isMagento;

  const loadGate = useCallback(async () => {
    try {
      const res = await retailerApi.stockGateStatus(retailer.retailer_id);
      if (res.success) setGate(res.data);
    } catch (e) { /* non-fatal — panel still renders the toggle */ }
  }, [retailer.retailer_id]);

  useEffect(() => { loadGate(); }, [loadGate]);

  const runCheck = async () => {
    setStatus('running');
    setError(null);
    setResult(null);
    try {
      const res = await retailerApi.checkReadiness(retailer.retailer_id);
      if (!res.success) {
        setError(res.error || 'Check failed');
        setStatus('error');
        return;
      }
      setResult(res.data);
      setStatus('done');
      loadGate(); // gate status may have moved (a fresh preflight verdict)
    } catch (e) {
      setError(e.body?.error || e.message || 'Check failed');
      setStatus('error');
    }
  };

  const clearSuspension = async () => {
    try {
      await retailerApi.clearSuspension(retailer.retailer_id);
      onChanged?.();
    } catch (e) {
      setError(e.body?.error || e.message || 'Failed to clear suspension');
    }
  };

  const suspendedUntil = retailer.crowdsource_stock_suspended_until;
  const isSuspended = suspendedUntil && new Date(suspendedUntil) > new Date();
  const drift = retailer.latest_drift_check;
  const driftInstock = retailer.latest_drift_check_instock;
  const spot = result?.spot_check;

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Stock-Veto Readiness
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {/* Regime is the primary identity here (which correction logic applies). */}
        <Chip
          size="small"
          color={isLiveApi ? 'secondary' : isChannable ? 'primary' : isMagento ? 'info' : 'default'}
          label={feedSource ? `${feedSource}${isLiveApi ? ' · API-arbitrated' : isChannable ? ' · crowd' : isMagento ? ' · intra-day parser' : ''}` : 'no feed_source'}
        />
        {/* storefront_platform is a separate axis from feed_source: it names the WEBSHOP
            software whose PDP DOM the pre-flight parser reads (Berden/BeOne/Janse are Divide
            webshops fed via Channable). Only the Channable pre-flight uses it. */}
        {isChannable && (
          retailer.storefront_platform
            ? <Chip size="small" variant="outlined" label={`storefront: ${retailer.storefront_platform} (pre-flight parser)`} />
            : <Chip size="small" color="warning" label="no storefront_platform set" />
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={runCheck}
          disabled={status === 'running'}
        >
          {status === 'running'
            ? (isApiCheck ? 'Checking API…' : 'Checking live PDPs…')
            : (isApiCheck ? 'Check API stock' : 'Check readiness')}
        </Button>
        <Tooltip
          arrow
          title={isMagento
            ? 'Samples served sizes (keyed by raw_sku) and asks Magento MSI directly (read-only). Mini-parser health check: disagreements should be near zero while the intra-day poll runs.'
            : isLiveApi
              ? 'Samples served sizes and asks the platform Admin API directly (read-only). Confirms the API is reachable and answering; disagreements vs the feed are expected (intra-day staleness).'
              : "Fetches the retailer's would-be day-one stock vetoes and re-parses each live PDP independently of the shipped script. Enabling the veto toggle requires a passing check."}
        >
          <HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>

        {isSuspended && (
          <Chip
            size="small"
            color="error"
            label={`Suspended until ${new Date(suspendedUntil).toLocaleString()}`}
          />
        )}
        {isSuspended && (
          <Button size="small" color="warning" onClick={clearSuspension}>
            Clear suspension
          </Button>
        )}

        {/* Weekly drift chips are a DOM re-parse — Channable-only. */}
        {isChannable && drift && (
          <Chip
            size="small"
            color={drift.verdict === 'pass' ? 'success' : 'error'}
            variant="outlined"
            label={`drift AV→SO ${drift.verdict}${drift.run_at ? ` · ${new Date(drift.run_at).toLocaleDateString()}` : ''}`}
          />
        )}
        {isChannable && driftInstock && (
          <Chip
            size="small"
            color={driftInstock.verdict === 'pass' ? 'success' : 'error'}
            variant="outlined"
            label={`drift SO→AV ${driftInstock.verdict}${driftInstock.run_at ? ` · ${new Date(driftInstock.run_at).toLocaleDateString()}` : ''}`}
          />
        )}
      </Stack>

      {/* Regime-appropriate gate / health status. */}
      {isChannable && <ChannableGates gate={gate?.channable} />}
      {isLiveApi && <LiveApiStatus status={gate?.live_api} />}
      {isMagento && <MagentoParserStatus status={gate?.parser} />}

      {status === 'running' && <LinearProgress sx={{ my: 1 }} />}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      {/* Channable pre-flight result: one block per direction. */}
      {result && isChannable && (
        <Box sx={{ mt: 1 }}>
          <DirectionResult
            title="Veto → sold-out (AV→SO)"
            expectation="Live page shows these sizes AVAILABLE (would be false vetoes):"
            block={result.veto}
          />
          <DirectionResult
            title="Veto → in-stock (SO→AV)"
            expectation="Live page shows these sizes SOLD OUT (would be false in-stock vetoes):"
            block={result.instock}
            sx={{ mt: 2 }}
          />
        </Box>
      )}

      {/* API spot-check result (live_api + magento). */}
      {spot && isApiCheck && (
        <Alert
          severity={spot.sampled === 0 ? 'info' : isMagento && spot.disagree > 0 ? 'warning' : 'success'}
          sx={{ mt: 1 }}
        >
          {spot.sampled === 0
            ? `Nothing to spot-check (${spot.note || 'no identifiable served sizes'}).`
            : `Spot-checked ${spot.sampled} served sizes against the API: ${spot.agree} agree, ${spot.disagree} disagree${isMagento ? ' (should be ~0 — the mini-parser keeps stock fresh)' : ' (intra-day stale — expected)'}, ${spot.unresolved} unresolved, 0 errors.`}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!applyEnabled}
              onChange={(e) => onToggleApply(e.target.checked)}
            />
          }
          label="Apply intra-day stock vetoes"
        />
        <Tooltip
          arrow
          title="When ON, intra-day stock corrections apply for this retailer — crowd-observed web sellouts for feed (Channable) retailers, real-time platform-API verdicts for Shopware/Shopify, and the availability mini-parser's direct stock refresh for Magento (no vetoes; the parser writes in_stock_overall itself)."
        >
          <HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
        {applyDirty && (
          <Button size="small" variant="contained" onClick={() => onSaveApply()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Box>
      {applyDirty && applyEnabled && isChannable && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Enabling is not gated on the live-PDP check. Review the shadow veto table and the daily feed audit first; the weekly drift read auto-suspends a retailer whose vetoes the live page contradicts.
        </Typography>
      )}
      {applyDirty && applyEnabled && isLiveApi && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          The platform API arbitrates every correction — no readiness check needed to enable.
        </Typography>
      )}
    </Paper>
  );
}

function descendingComparator(a, b, orderBy) {
  const valA = a[orderBy];
  const valB = b[orderBy];
  if (valA == null && valB == null) return 0;
  if (valA == null) return 1;
  if (valB == null) return -1;
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SORTABLE_COLUMNS = [
  { id: 'company_name', label: 'Retailer' },
  { id: 'status', label: 'Status' },
  { id: 'latest_heartbeat_at', label: 'Frontend Heartbeat' },
  { id: 'script_version', label: 'Script Version' },
  { id: 'is_enabled', label: 'Widget Enabled', align: 'center' },
];

function OverviewTab({ retailers, loading, onRefresh, onSelectRetailer }) {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [orderBy, setOrderBy] = useState('company_name');
  const [order, setOrder] = useState('asc');

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const statuses = useMemo(() => {
    const set = new Set(RETAILER_STATUSES);
    retailers.forEach((r) => set.add(r.status || 'unknown'));
    return ['all', ...Array.from(set).sort()];
  }, [retailers]);

  const filtered = useMemo(() => {
    let result = retailers;

    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter((r) => (r.status || 'unknown') === statusFilter);
    }

    // Text search
    if (filter) {
      const search = filter.toLowerCase();
      result = result.filter(
        (r) =>
          r.retailer_id.toLowerCase().includes(search) ||
          (r.company_name || '').toLowerCase().includes(search)
      );
    }

    // Sort
    return result.slice().sort(getComparator(order, orderBy));
  }, [retailers, statusFilter, filter, order, orderBy]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
              displayEmpty
              notched
            >
              <MenuItem value="all">All ({retailers.length})</MenuItem>
              {statuses.filter((s) => s !== 'all').map((s) => (
                <MenuItem key={s} value={s}>
                  {capitalize(s)} ({retailers.filter((r) => (r.status || 'unknown') === s).length})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search retailers..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: 250 } }}
          />
          <Typography variant="body2" color="text.secondary">
            {filtered.length} retailer{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {SORTABLE_COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r) => (
              <TableRow
                key={r.retailer_id}
                sx={{
                  '&:hover': { backgroundColor: 'action.hover' },
                  opacity: r.is_enabled ? 1 : 0.6,
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {r.company_name || r.retailer_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.retailer_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                </TableCell>
                <TableCell>
                  <HeartbeatStatus
                    latestAt={r.latest_heartbeat_at}
                    heartbeatPages={r.heartbeat_pages}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {r.script_version || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <OverrideToggle
                    retailerId={r.retailer_id}
                    isEnabled={r.is_enabled}
                    onToggle={onRefresh}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => onSelectRetailer(r.retailer_id)}>
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const DOMAIN_TYPES = ['storefront', 'alias', 'platform_id', 'admin'];
const LANGUAGE_ROW_TYPES = ['storefront', 'alias'];

// Prefix map <-> the compact text the Domains table edits: "en=/en/, de=/de/".
function formatPrefixes(map) {
  return Object.entries(map || {}).map(([code, prefix]) => `${code}=${prefix}`).join(', ');
}
function parsePrefixes(text) {
  const map = {};
  (text || '').split(',').map((part) => part.trim()).filter(Boolean).forEach((part) => {
    const [code, prefix] = part.split('=').map((x) => (x || '').trim());
    if (!/^[a-z]{2}$/.test(code) || !/^\/[a-z]{2}(-[a-z]{2})?\/$/.test(prefix)) {
      throw new Error(`"${part}" — use language=/xx/ (for example en=/en/, de=/de/)`);
    }
    map[code] = prefix;
  });
  return Object.keys(map).length ? map : null;
}

// A storefront is one row: the language at its bare path and the "/xx/" prefixes of its other
// languages (migration 228). japantky.nl is nl + en=/en/, de=/de/; japantky.de is de + en=/en/, nl=/nl/.
function DomainsSection({ retailerId, onChanged }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newType, setNewType] = useState('storefront');
  const [newLanguage, setNewLanguage] = useState('');
  const [adding, setAdding] = useState(false);
  const [prefixDrafts, setPrefixDrafts] = useState({});

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await retailerApi.listDomains(retailerId);
      setDomains(data.domains || []);
      setPrefixDrafts({});
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [retailerId]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  const refresh = async () => {
    await loadDomains();
    if (onChanged) onChanged();
  };

  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    if (newType === 'storefront' && !newLanguage) {
      setError('A storefront domain needs the language served at its bare path.');
      return;
    }
    setAdding(true);
    setError('');
    try {
      const extra = LANGUAGE_ROW_TYPES.includes(newType) && newLanguage ? { language: newLanguage } : {};
      await retailerApi.addDomain(retailerId, newDomain.trim(), newType, extra);
      setNewDomain('');
      setNewType('storefront');
      setNewLanguage('');
      await refresh();
    } catch (err) {
      setError(err.message);
    }
    setAdding(false);
  };

  const handleDelete = async (domain) => {
    setError('');
    try {
      await retailerApi.deleteDomain(retailerId, domain);
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdate = async (domain, fields) => {
    setError('');
    try {
      await retailerApi.updateDomain(retailerId, domain, fields);
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const savePrefixes = async (d) => {
    const draft = prefixDrafts[d.domain];
    if (draft === undefined || draft === formatPrefixes(d.localized_paths)) return;
    try {
      await handleUpdate(d.domain, { localized_paths: parsePrefixes(draft) });
    } catch (err) {
      setError(err.message);
    }
  };

  const languageLabel = (code) => (LANGUAGE_NAME_BY_CODE[code] ? `${code} — ${LANGUAGE_NAME_BY_CODE[code]}` : code);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Domains
        </Typography>
        <Tooltip
          title={
            'One row per storefront host. Language = what the bare path serves (check <html lang> or the ' +
            "hreflang tags). Other languages = the /xx/ prefixes on that host, e.g. en=/en/, de=/de/. " +
            'The script, the overlay language and the partner links are generated from these rows.'
          }
          arrow
        >
          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', mb: 0.75 }} />
        </Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Other languages</TableCell>
              <TableCell>Canonical</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((d) => {
              const carriesLanguage = LANGUAGE_ROW_TYPES.includes(d.domain_type);
              const missingLanguage = d.domain_type === 'storefront' && !d.language;
              const draft = prefixDrafts[d.domain] ?? formatPrefixes(d.localized_paths);
              return (
                <TableRow key={d.domain}>
                  <TableCell><code>{d.domain}</code></TableCell>
                  <TableCell>{d.domain_type}</TableCell>
                  <TableCell>
                    {carriesLanguage ? (
                      <FormControl size="small" error={missingLanguage} sx={{ minWidth: 150 }}>
                        <Select
                          value={d.language || ''}
                          displayEmpty
                          onChange={(e) => handleUpdate(d.domain, { language: e.target.value })}
                          renderValue={(v) => (v ? languageLabel(v) : <em>{missingLanguage ? 'required' : 'inherit'}</em>)}
                        >
                          {LANGUAGE_OPTIONS.map((l) => (
                            <MenuItem key={l.code} value={l.code}>{languageLabel(l.code)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {carriesLanguage ? (
                      <TextField
                        size="small"
                        placeholder="en=/en/, de=/de/"
                        value={draft}
                        onChange={(e) => setPrefixDrafts((prev) => ({ ...prev, [d.domain]: e.target.value }))}
                        onBlur={() => savePrefixes(d)}
                        onKeyDown={(e) => e.key === 'Enter' && savePrefixes(d)}
                        sx={{ minWidth: 190 }}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {d.is_canonical ? (
                      <StarIcon fontSize="small" color="warning" />
                    ) : d.domain_type === 'storefront' ? (
                      <Tooltip title="Set as canonical">
                        <IconButton size="small" onClick={() => handleUpdate(d.domain, { is_canonical: true })}>
                          <StarIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </IconButton>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {!d.is_canonical && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(d.domain)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <FormControl size="small" fullWidth>
                  <Select value={newType} onChange={(e) => setNewType(e.target.value)}>
                    {DOMAIN_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                {LANGUAGE_ROW_TYPES.includes(newType) && (
                  <FormControl size="small" sx={{ minWidth: 150 }} error={newType === 'storefront' && !newLanguage}>
                    <Select
                      value={newLanguage}
                      displayEmpty
                      onChange={(e) => setNewLanguage(e.target.value)}
                      renderValue={(v) => (v ? languageLabel(v) : <em>language</em>)}
                    >
                      {LANGUAGE_OPTIONS.map((l) => (
                        <MenuItem key={l.code} value={l.code}>{languageLabel(l.code)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={adding || !newDomain.trim() || (newType === 'storefront' && !newLanguage)}
                >
                  Add
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

// One intro line per language the retailer's storefronts serve (retailer_intro_lines).
function IntroLinesEditor({ languages, value, onChange }) {
  if (!languages.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
        Set a language on a storefront domain first (Domains tab); the intro line is written per language.
      </Typography>
    );
  }
  const lineFor = (code) => (value || []).find((l) => l.language === code)?.line || '';
  const setLine = (code, line) =>
    onChange(languages.map((c) => ({ language: c, line: c === code ? line : lineFor(c) })));
  return languages.map((code) => (
    <IntroLineField
      key={code}
      label={`Intro line (${LANGUAGE_NAME_BY_CODE[code] || code})`}
      value={lineFor(code)}
      onChange={(line) => setLine(code, line)}
    />
  ));
}

const DETAIL_TABS = [
  { key: 'heartbeats', label: 'Heartbeats' },
  { key: 'domains', label: 'Domains & Integration' },
  { key: 'app', label: 'App Settings' },
  { key: 'billing', label: 'Billing Settings' },
];

function DetailTab({ retailerId, tab, onTabChange, onBack }) {
  const [retailer, setRetailer] = useState(null);
  const [heartbeats, setHeartbeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [edits, setEdits] = useState({});

  useEffect(() => {
    loadDetail();
  }, [retailerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // savedKeys: after a per-tab save, only those keys leave `edits`; the other tabs keep
  // their unsaved changes (one form, four Save buttons).
  const loadDetail = async (savedKeys = null) => {
    setLoading(true);
    setError('');
    try {
      const data = await retailerApi.getDetail(retailerId);
      setRetailer(data.retailer);
      setHeartbeats(data.heartbeats || []);
      setEdits((prev) => {
        if (!savedKeys) return {};
        const next = { ...prev };
        savedKeys.forEach((k) => delete next[k]);
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleFieldChange = (field, value) => {
    setEdits((prev) => ({ ...prev, [field]: value }));
  };

  // Save the edits that belong to one tab (or the header), leaving the others pending.
  const handleSaveKeys = async (keys, label) => {
    const payload = {};
    keys.forEach((k) => {
      if (k in edits) payload[k] = edits[k];
    });
    if (Object.keys(payload).length === 0) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await retailerApi.update(retailerId, payload);
      setSuccess(`${label} saved`);
      await loadDetail(Object.keys(payload));
    } catch (err) {
      setError(err.body?.error || err.message);
    }
    setSaving(false);
  };

  // Save just the apply_intraday_stock_vetoes toggle (from the readiness panel). Kept
  // separate from the field-grid Save so enabling the veto doesn't also flush unrelated
  // pending edits. (The server-side pre-flight gate and its override were retired 2026-09-22.)
  const handleSaveApply = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = { apply_intraday_stock_vetoes: !!edits.apply_intraday_stock_vetoes };
      await retailerApi.update(retailerId, payload);
      setSuccess('Stock-veto setting saved');
      await loadDetail();
    } catch (err) {
      setError(err.body?.error || err.message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!retailer) {
    return <Alert severity="error">Retailer not found</Alert>;
  }

  const editableFields = [
    { tab: 'header', key: 'company_name', label: 'Company Name', type: 'text' },
    { tab: 'header', key: 'contact_email', label: 'Contact Email', type: 'text' },
    { tab: 'header', key: 'contact_name', label: 'Contact Name', type: 'text' },
    { tab: 'header', key: 'status', label: 'Status', type: 'select', options: RETAILER_STATUSES },
    { tab: 'billing', key: 'commission_rate', label: 'HandOff share of partner commission (%)', type: 'number', min: 0, max: 100,
      tooltip: 'Of the commission the receiving retailer pays on a partner handoff (its bid, 10% of the clicked item by default), HandOff keeps this share and the sending retailer gets the rest. 20 means 20% of the 10% = 2% of the order for HandOff, 8% for the sender. Not a share of the order.' },
    { tab: 'app', key: 'alternatives_enabled', label: 'Alternatives (own-store suggestions)', type: 'boolean',
      tooltip: 'Pilot opt-in (be-one.nl). With this on, the overlay can offer the retailer\'s own alternatives for a sold-out size, and the nightly job scores this retailer\'s candidate sets. The frontend (WS4) is not built yet: enabling only switches the data and the API on. Spec: .claude-product-specs/overlay/cross-sell-overlay-variant.md.' },
    { tab: 'app', key: 'alternatives_label_no_partner_enabled', label: 'Alternatives label on sizes without a partner', type: 'boolean',
      tooltip: 'Sub-setting of Alternatives: sold-out sizes with no partner match get a "SEE ALTERNATIVES" label that opens the overlay in alternatives-only mode. Only read when Alternatives is on.' },
    { tab: 'billing', key: 'alternatives_commission_rate', label: 'Alternatives commission (% of order value, HandOff keeps all)', type: 'number',
      tooltip: 'What the retailer pays HandOff on an order that followed a click on one of its own alternatives (default 2%). Frozen on each click, so a change never rewrites past clicks. Nothing is paid out to a partner on these orders.' },
    { tab: 'domains', key: 'retailer_type', label: 'Retailer Type', type: 'select', options: [...RETAILER_TYPES, null], rawOptionLabels: true,
      tooltip: 'Business model, not platform: MULTI_BRAND (a store carrying many brands) vs DIRECT_BRAND (a single brand\'s own store). Feeds code-extraction. Distinct from Storefront Platform (rendering) and feed source (ingestion).' },
    { tab: 'domains', key: 'ships_internationally', label: 'Ships Internationally', type: 'boolean',
      tooltip: 'Shipping zones are checked first — if this retailer has zones matching the visitor\'s country, it\'s shown as a match. Ships Internationally acts as a fallback: if no zone matches, the retailer is still included when this is enabled. Both can be active together.' },
    { tab: 'app', key: 'has_separate_webshop_stock', label: 'Separate Webshop Stock', type: 'boolean' },
    { tab: 'app', key: 'hide_discounted_matches_on_full_price', label: 'Hide discounted partner matches when our product isn\'t on sale', type: 'boolean',
      tooltip: 'When ON, partner matches showing a discounted price are hidden on this retailer\'s product pages — but only when their own product is at full price. If their own product is on sale, all matches are shown. Note: hides every match for any partner currently running a temporary site-wide promo (intentional — that\'s what the toggle is for). See .claude-product-specs/hide-discounted-matches-when-full-price.md.' },
    { tab: 'app', key: 'hide_deeper_discounts_when_on_sale', label: 'Hide partner matches discounting deeper than our products', type: 'boolean',
      tooltip: 'When ON (and prerequisite is also ON), partner matches that are on sale are hidden if their discount percentage exceeds ours by more than the threshold — but only when our own product is also on sale. If our product is at full price, the prerequisite rule applies instead (any partner discount is hidden). Requires "Hide discounted partner matches when our product isn\'t on sale" to be ON. See .claude-product-specs/hide-deeper-discounts-when-on-sale.md.',
      disabledWhen: (getValue) => !getValue('hide_discounted_matches_on_full_price') },
    { tab: 'app', key: 'deeper_discount_threshold_pp', label: 'Deeper-discount threshold (percentage points)', type: 'number',
      tooltip: 'Strict greater-than threshold. Default 10pp (e.g. you at 20% off: partners at 31%+ off are hidden). Range 5–100; the retailer can also set it on Settings › Visibility. Only consulted when the deeper-discount toggle above is ON.',
      disabledWhen: (getValue) => !getValue('hide_deeper_discounts_when_on_sale'),
      gridColumn: 2,  // pin under its parent toggle (which sits in col 2)
      min: 0, max: 100 },
    // Never-stocked partner sizes (migration 215). A matching policy, not a dashboard setting:
    // it changes what return-matches returns on the shop, so it is not tied to the Buyer
    // Insights switch. The retailer's "Sizes you never bought in to" view follows it.
    { tab: 'app', key: 'offer_never_stocked_sizes', label: 'Offer sizes this retailer never stocked', type: 'boolean',
      tooltip: 'When ON, partner sizes this retailer never stocked (no size row ever, never seen on its page) are offered on its product pages, up to the cap below. When OFF, only sizes it has stocked are handed off. Needs size_injection ON in the retailer\'s script config; otherwise it does nothing either way. Audited (retailer_policy_audit). Spec .claude-product-specs/overlay/never-stocked-sizes-opt-in.md.' },
    { tab: 'app', key: 'never_stocked_max_sizes', label: 'Never-stocked sizes cap (per product page)', type: 'number',
      tooltip: 'At most this many never-stocked sizes per page; the ones closest to the middle of the size curve win. Counts never-stocked sizes only, never the retailer\'s own sold-out sizes. Empty = no cap. Replaces max_inserted_sizes in the script config.',
      disabledWhen: (getValue) => !getValue('offer_never_stocked_sizes'),
      gridColumn: 2,
      min: 1 },
    { tab: 'domains', key: 'feed_source', label: 'Feed Source', type: 'select', rawOptionLabels: true,
      options: [...FEED_SOURCES, null],
      tooltip: 'How stock/catalogue is INGESTED — sets the intra-day correction regime: channable = crowd-observed web sellouts (DOM pre-flight gates the veto toggle); shopware/shopify = live platform Admin API arbitrates; magento = availability mini-parser refreshes stock directly. Distinct from Storefront Platform (how the PDP renders). Leave unset only for retailers with no intra-day correction.' },
    { tab: 'domains', key: 'storefront_platform', label: 'Storefront Platform', type: 'select', rawOptionLabels: true,
      // Same values as the retailers_storefront_platform_check constraint (migration 211).
      options: ['divide', 'factif', 'magento', 'nextchapter', 'scayle', 'shopware', 'shopware-nuxt', 'shopify', null],
      tooltip: 'How this retailer\'s PDP renders — drives the stock pre-flight checker\'s parser routing (distinct from feed source). shopware = classic SSR Storefront, including stores running the Squeezely back-in-stock plugin; shopware-nuxt = headless Nuxt SPA (different parser). Set correctly before running a readiness check. factif = Factif ShopMaster (Arthur & Willemijn); scayle = Scayle backend with a Nuxt storefront (Van Tilburg). See migrations 093, 122, 141 and 211.' },
    { tab: 'app', key: 'overlay_renderer', label: 'Overlay (own PDPs)', type: 'select', rawOptionLabels: true,
      options: OVERLAY_RENDERERS,
      tooltip: 'Which overlay shoppers see on THIS retailer\'s product pages. legacy = today\'s overlay; trust = the new overlay. Flipping back to legacy is the kill switch (no deploy). Rollout switch only — removed once every retailer is on trust.' },
    // apply_intraday_stock_vetoes is NOT here — it lives in the Stock-Veto Readiness panel,
    // next to the pre-flight check that gates it (see StockReadinessPanel).
  ];

  const getFieldValue = (key) => {
    return key in edits ? edits[key] : (retailer[key] ?? '');
  };

  // Auto-reset dependent fields when their prerequisite is turned off, to
  // prevent stuck-state UX (e.g. the backend would reject the save with a 400
  // anyway; better to clean up the dependent fields proactively in the UI).
  const handleFieldChangeWithCascade = (key, value) => {
    handleFieldChange(key, value);
    if (key === 'hide_discounted_matches_on_full_price' && !value) {
      handleFieldChange('hide_deeper_discounts_when_on_sale', false);
    }
  };

  // Languages and URL paths live on the storefront rows (Domains table); intro lines are one per
  // storefront language and travel with the App tab's save.
  const storefrontLanguages = retailer?.storefront_languages || [];

  // ---- Tabs (Daniel, 2026-10-23): the page was eight screens of scroll with one Save at the
  // bottom. Four tabs, each with its own Save; the tab is in the URL (?tab=) so other pages can
  // link straight to Billing Settings; a dot on a tab = unsaved changes there.
  const keysOf = (tab) => editableFields.filter((f) => f.tab === tab).map((f) => f.key);
  const TAB_KEYS = {
    header: keysOf('header'),
    domains: [...keysOf('domains'), 'shipping_zones'],
    app: [...keysOf('app'), 'intro_lines'],
    billing: keysOf('billing'),
    heartbeats: [],
  };
  const dirty = (tab) => TAB_KEYS[tab].some((k) => k in edits);
  const renderField = (field) => {
    const isDisabled = field.disabledWhen ? field.disabledWhen(getFieldValue) : false;
    if (field.type === 'boolean') {
      return (
        <Box key={field.key} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!getFieldValue(field.key)}
                disabled={isDisabled}
                onChange={(e) => handleFieldChangeWithCascade(field.key, e.target.checked)}
              />
            }
            label={field.label}
          />
          {field.tooltip && (
            <Tooltip title={field.tooltip} arrow>
              <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
            </Tooltip>
          )}
        </Box>
      );
    }
    if (field.type === 'select') {
      return (
        <FormControl key={field.key} size="small" fullWidth disabled={isDisabled}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={getFieldValue(field.key) || ''}
            label={field.label}
            onChange={(e) => handleFieldChange(field.key, e.target.value || null)}
          >
            {field.options.map((opt) => (
              <MenuItem key={opt || 'none'} value={opt || ''}>
                {opt ? (field.rawOptionLabels ? opt : capitalize(opt)) : '(none)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    return (
      <Box key={field.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...(field.gridColumn ? { gridColumn: field.gridColumn } : {}) }}>
        <TextField
          size="small"
          label={field.label}
          value={getFieldValue(field.key)}
          disabled={isDisabled}
          onChange={(e) => handleFieldChange(field.key, field.type === 'number' ? parseFloat(e.target.value) || null : e.target.value)}
          type={field.type === 'number' ? 'number' : 'text'}
          inputProps={field.type === 'number' ? { min: field.min, max: field.max } : undefined}
          fullWidth
        />
        {field.tooltip && (
          <Tooltip title={field.tooltip} arrow>
            <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>
    );
  };
  const fieldGrid = (keys) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
      {editableFields.filter((f) => keys.includes(f.key)).map(renderField)}
    </Box>
  );
  const saveButton = (tab, label, extraDisabled = false) => (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={() => handleSaveKeys(TAB_KEYS[tab], label)}
        disabled={saving || !dirty(tab) || extraDisabled}
      >
        {saving ? 'Saving...' : `Save ${label.toLowerCase()}`}
      </Button>
    </Box>
  );
  const section = (title, children) => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>{title}</Typography>
      {children}
    </Paper>
  );

  // Header facts: is this shop alive. Script version and last heartbeat from the product
  // page rows (the ones that matter for the overlay), falling back to any page.
  const productHb = heartbeats.find((hb) => hb.page_type === 'product') || heartbeats[0];
  const lastSeen = heartbeats.reduce((m, hb) => (hb.last_seen_at && (!m || hb.last_seen_at > m) ? hb.last_seen_at : m), null);
  const hbAgeHours = lastSeen ? (Date.now() - new Date(lastSeen).getTime()) / 36e5 : null;

  // Partner economics, worked out from the stored numbers. The bid is retailers.default_bid, 10 for
  // every retailer: bidding is built but switched off (Daniel, 2026-10-23), so it is shown, not
  // edited; HandOff's share is the editable field below. 10 and 20 -> 2 and 8.
  const bid = Number(retailer.default_bid ?? 0);
  const share = Number(getFieldValue('commission_rate') || 0);
  const handoffPct = (bid * share) / 100;
  const altRate = Number(getFieldValue('alternatives_commission_rate') || 0);

  const activeTab = DETAIL_TABS.some((t) => t.key === tab) ? tab : 'heartbeats';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography variant="h6">
          {retailer.company_name || retailer.retailer_id}
        </Typography>
        <Chip label={retailer.retailer_id} size="small" variant="outlined" />
        <Chip label={capitalize(retailer.status || 'unknown')} size="small" color={retailer.status === 'active' ? 'success' : 'default'} variant="outlined" />
        {retailer.retailer_group && <Chip label={`group: ${retailer.retailer_group}`} size="small" variant="outlined" />}
        <Chip
          label={retailer.override_is_enabled ? 'widget on' : 'widget OFF'}
          size="small"
          color={retailer.override_is_enabled ? 'success' : 'error'}
        />
        {productHb?.script_version && <Chip label={`script ${productHb.script_version}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />}
        {lastSeen && (
          <Chip
            label={`heartbeat ${new Date(lastSeen).toLocaleString()}`}
            size="small"
            color={hbAgeHours > STALE_HOURS ? 'warning' : 'default'}
            variant="outlined"
          />
        )}
      </Box>

      {/* Identity, always visible and saved on its own */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {fieldGrid(TAB_KEYS.header)}
        {dirty('header') && saveButton('header', 'Contact details')}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => onTabChange(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {DETAIL_TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Badge color="warning" variant="dot" invisible={!dirty(t.key)} sx={{ '& .MuiBadge-badge': { right: -8, top: 2 } }}>
                {t.label}
              </Badge>
            }
          />
        ))}
      </Tabs>

      {activeTab === 'heartbeats' && (
        <>
          {section('Heartbeat Status', heartbeats.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No heartbeats received</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page Type</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Script Version</TableCell>
                  <TableCell>Count</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {heartbeats.map((hb, i) => (
                  <TableRow key={i}>
                    <TableCell>{hb.page_type}</TableCell>
                    <TableCell>{(hb.actions || []).join(', ') || '—'}</TableCell>
                    <TableCell><code>{hb.script_version || '—'}</code></TableCell>
                    <TableCell>{hb.heartbeat_count}</TableCell>
                    <TableCell>{hb.first_seen_at ? new Date(hb.first_seen_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{hb.last_seen_at ? new Date(hb.last_seen_at).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))}
          {section('Emergency Override', (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <OverrideToggle
                retailerId={retailer.retailer_id}
                isEnabled={retailer.override_is_enabled}
                onToggle={() => loadDetail(Object.keys(edits).length ? [] : null)}
              />
              <Typography variant="body2">
                {retailer.override_is_enabled ? 'Widget is enabled' : 'Widget is DISABLED'}
              </Typography>
              {retailer.override_reason && (
                <Typography variant="body2" color="text.secondary">
                  Reason: {retailer.override_reason}
                </Typography>
              )}
            </Box>
          ))}
          <StockReadinessPanel
            retailer={retailer}
            onChanged={() => loadDetail([])}
            applyEnabled={getFieldValue('apply_intraday_stock_vetoes')}
            applyDirty={'apply_intraday_stock_vetoes' in edits}
            onToggleApply={(v) => handleFieldChange('apply_intraday_stock_vetoes', v)}
            onSaveApply={handleSaveApply}
            saving={saving}
          />
        </>
      )}

      {activeTab === 'domains' && (
        <>
          <DomainsSection retailerId={retailerId} onChanged={() => loadDetail()} />
          {section('Integration', (
            <>
              {fieldGrid(keysOf('domains'))}
              {/* Shipping Zones */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Shipping Zones</Typography>
                  <Tooltip title="Specific countries this retailer ships to. Checked first when matching — visitors from these countries see this retailer. 'Ships Internationally' is a separate fallback: when enabled, the retailer is also shown to visitors from countries not in this list." arrow>
                    <HelpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Autocomplete
                  multiple
                  size="small"
                  options={COUNTRY_OPTIONS.map((c) => c.code)}
                  value={edits.shipping_zones || retailer.shipping_zones || []}
                  onChange={(e, newValue) => handleFieldChange('shipping_zones', [...newValue].sort())}
                  getOptionLabel={(code) => (COUNTRY_NAME_BY_CODE[code] ? `${code} — ${COUNTRY_NAME_BY_CODE[code]}` : code)}
                  renderTags={(value, getTagProps) =>
                    value.map((code, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} label={code} size="small" variant="outlined" {...tagProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Add countries…" />
                  )}
                  sx={{ width: { xs: '100%', sm: 500 } }}
                />
              </Box>

              {saveButton('domains', 'Integration settings')}
            </>
          ))}
          <Paper sx={{ p: 2, mb: 3 }}>
            <ApiKeysPanel retailer={retailer} />
          </Paper>
        </>
      )}

      {activeTab === 'app' && (
        <>
          {section('Overlay', (
            <>
              {fieldGrid(['overlay_renderer'])}
              <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                <IntroLinesEditor
                  languages={storefrontLanguages}
                  value={edits.intro_lines ?? retailer.intro_lines ?? []}
                  onChange={(v) => handleFieldChange('intro_lines', v)}
                />
              </Box>
            </>
          ))}
          {section('Matching policy', fieldGrid([
            'hide_discounted_matches_on_full_price', 'hide_deeper_discounts_when_on_sale', 'deeper_discount_threshold_pp',
            'offer_never_stocked_sizes', 'never_stocked_max_sizes', 'has_separate_webshop_stock',
          ]))}
          {section('Pilots', (
            <>
              {fieldGrid(['alternatives_enabled', 'alternatives_label_no_partner_enabled'])}
              {saveButton('app', 'App settings')}
            </>
          ))}
        </>
      )}

      {activeTab === 'billing' && (
        <>
          {section('Commission', (
            <>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Partner handoff:</strong> the receiving retailer pays <strong>{bid}%</strong> of the clicked item
                (the network-wide bid; retailer bidding is switched off) · HandOff keeps <strong>{share}%</strong> of that
                = <strong>{handoffPct.toFixed(2)}%</strong> of the order · the sending retailer earns <strong>{(bid - handoffPct).toFixed(2)}%</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Own-store alternative:</strong> this retailer pays <strong>{altRate}%</strong> of the order to HandOff; there is no sending partner, so nothing is paid out.
                Both rates are frozen on each click, so a change here never rewrites a past click.
                Versions: bid {retailer.bid_version ?? '—'} · HandOff share {retailer.commission_version ?? '—'} · alternatives {retailer.alternatives_commission_version ?? '—'}.
              </Typography>
              {fieldGrid(TAB_KEYS.billing)}
              {saveButton('billing', 'Commission settings')}
            </>
          ))}
          <Paper sx={{ p: 2, mb: 3 }}>
            <BalanceBandPanel retailer={retailer} onChanged={() => loadDetail([])} />
          </Paper>
          <Paper sx={{ p: 2, mb: 3 }}>
            <BillingEntityPanel retailer={retailer} onChanged={() => loadDetail([])} />
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              System Info (read-only)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
              <Typography variant="body2">Created: {retailer.created_at ? new Date(retailer.created_at).toLocaleString() : '—'}</Typography>
              <Typography variant="body2">Updated: {retailer.updated_at ? new Date(retailer.updated_at).toLocaleString() : '—'}</Typography>
              <Typography variant="body2">Firebase Org: {retailer.firebase_organization_id || '—'}</Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default function RetailersDashboard() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [params, setParams] = useSearchParams();
  // ?retailer=<id>&tab=<key>: the detail page is addressable, so Commissions or Invoicing can
  // link straight to a retailer's Billing Settings (Daniel, 2026-10-23).
  const selectedRetailer = params.get('retailer');
  const tab = params.get('tab') || 'heartbeats';
  const [error, setError] = useState('');

  const loadRetailers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await retailerApi.list();
      setRetailers(data.retailers || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRetailers();
  }, [loadRetailers]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (selectedRetailer) {
    return (
      <DetailTab
        retailerId={selectedRetailer}
        tab={tab}
        onTabChange={(t) => setParams({ retailer: selectedRetailer, tab: t })}
        onBack={() => {
          setParams({});
          loadRetailers();
        }}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Retailers
      </Typography>
      <OverviewTab
        retailers={retailers}
        loading={loading}
        onRefresh={loadRetailers}
        onSelectRetailer={(id) => setParams({ retailer: id, tab: 'heartbeats' })}
      />
    </Box>
  );
}
